import { useSelector } from "react-redux";
import { useThunkDispatch } from "setup/redux/thunkDispatch";
import { selectClientID } from "../ContactDetailsSlice";
import SimpleModal from "cpl-react-common/components/Modals/SimpleModal";
import { ErrorMessage, Field, Form, Formik } from "formik";
import * as Yup from "yup";
import { createNewClientEmail, selectNewEmail } from "./ClientEmailAddressesSlice";

type NewEmailModalProps = {
  ShowNewEmailModal: boolean;
  DismissNewEmailModalCallback(): void;
};

const getValidationSchema = () =>
  Yup.object().shape({
    emailAddress: Yup.string()
      .email("Wrong email format")
      .min(3, "Minimum 3 symbols")
      .max(50, "Maximum 50 symbols")
      .required("Email is required"),
  });

const NewClientEmailModal: React.FC<NewEmailModalProps> = ({ ShowNewEmailModal, DismissNewEmailModalCallback }) => {
  const thunkDispatch = useThunkDispatch();
  const newEmailValues = useSelector(selectNewEmail);
  const clientAccountNumber = useSelector(selectClientID);
  return (
    <SimpleModal
      modalTitle="New Contact Email"
      showModal={ShowNewEmailModal}
      dismissModalCallback={() => {
        DismissNewEmailModalCallback();
      }}
    >
      <div className="d-flex m-2 p-2 card">
        <Formik
          initialValues={newEmailValues}
          validationSchema={getValidationSchema()}
          onSubmit={(values) => {
            thunkDispatch(createNewClientEmail({ newClientEmail: values }));
            DismissNewEmailModalCallback();
          }}
          enableReinitialize
        >
          <Form>
            <Field name="clientAccountNumber" type="hidden" value={clientAccountNumber} />
            <div className="row">
              <div className="col-4">
                <label htmlFor="newEmailAddress" className="col-form-label fw-bold required">
                  Email Address
                </label>
              </div>
              <div className="col-8">
                <div className="fv-plugins-message-container">
                  <div className="fv-help-block">
                    <ErrorMessage name="emailAddress" />
                  </div>
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col">
                <Field
                  data-test-id="new-email-modal__email-address-input"
                  name="emailAddress"
                  className="form-control form-control-solid"
                  placeholder="John.Smith@CPLPropertyManagement.com"
                  aria-placeholder="John.Smith@CPLPropertyManagement.com"
                />
              </div>
            </div>
            <div className="row">
              <div className="col">
                <label htmlFor="newDescription" className="col-form-label fw-bold">
                  Description
                </label>
              </div>
              <div className="row">
                <Field
                  data-test-id="new-email-modal__description-input"
                  name="description"
                  className="form-control form-control-solid"
                />
              </div>
            </div>
            <div className="row">
              <div className="col-8">
                <div className="row mt-1">
                  <div className="col align-self-center">
                    <Field
                      data-test-id="new-email-modal__set-default-checkbox"
                      name="isDefault"
                      type="checkbox"
                      className="form-check-input"
                    />
                    <label htmlFor="isNewDefault" className="form-check-label ms-2 fw-bold">
                      Set as default?
                    </label>
                  </div>
                </div>
                <div className="row mt-1">
                  <div className="col align-self-center">
                    <Field
                      data-test-id="new-email-modal__use-for-documents-checkbox"
                      name="useForDocuments"
                      type="checkbox"
                      className="form-check-input"
                    />
                    <label htmlFor="useForNewDocuments" className="form-check-label ms-2 fw-bold">
                      Use for documents?
                    </label>
                  </div>
                </div>
                <div className="row mt-1">
                  <div className="col align-self-center">
                    <Field
                      data-test-id="new-email-modal__use-for-invoices-checkbox"
                      name="useForInvoices"
                      type="checkbox"
                      className="form-check-input"
                    />
                    <label htmlFor="useForNewInvoices" className="form-check-label ms-2 fw-bold">
                      Use For Invoices?
                    </label>
                  </div>
                </div>
              </div>
              <div className="col-4 align-self-end">
                <button
                  data-test-id="new-email-modal__save-button"
                  aria-label="Save"
                  type="submit"
                  className="cpl-btn cpl-btn-success m-1"
                >
                  Save
                </button>
                <button
                  data-test-id="new-email-modal__cancel-button"
                  aria-label="Cancel"
                  type="button"
                  className="cpl-btn cpl-btn-secondary m-1"
                  onClick={() => DismissNewEmailModalCallback()}
                >
                  Cancel
                </button>
              </div>
            </div>
          </Form>
        </Formik>
      </div>
    </SimpleModal>
  );
};

export default NewClientEmailModal;
